import React from "react";

import { useMutation } from "@apollo/client";
import Hidden from "@material-ui/core/Hidden";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import PropTypes from "prop-types";

import RbActions from "components/RbActions";
import RbButtonAction from "components/RbButtonAction";
import RbFlexTableCell from "components/RbFlexTableCell";
import RbFlexTableRow from "components/RbFlexTableRow";
import { deleteCarMutation, carListQuery } from "graphql/car";
import useAlert from "hooks/useAlert";
import useFormatters from "hooks/useFormatters";
import useMatchBreakpoints from "hooks/useMatchBreakpoints";
import useSnackbar from "hooks/useSnackbar";

import CarForm from "../CarForm";

function CarRow(props) {
  const { car } = props;

  const { openSnackbar } = useSnackbar();

  const { openAlert, closeAlert } = useAlert();

  const { formatCurrency } = useFormatters();

  const actionsFlex = useMatchBreakpoints({ xs: "0 48px", md: "0 88px" });

  const actionsLimit = useMatchBreakpoints({ xs: 0, md: 2 });

  const [expanded, setExpanded] = React.useState(false);

  const [deleteCar] = useMutation(deleteCarMutation, {
    onCompleted() {
      closeAlert();
      openSnackbar({
        message: "Car deleted successfully!",
      });
    },
    variables: {
      input: { id: car.id },
    },
    update(cache, result) {
      const { carList } = cache.readQuery({ query: carListQuery });
      cache.writeQuery({
        query: carListQuery,
        data: {
          carList: carList.filter((c) => c.id !== result.data.deleteCar.id),
        },
      });
    },
  });

  const handleEdit = React.useCallback(() => {
    setExpanded(true);
  }, []);

  const handleDelete = React.useCallback(() => {
    openAlert({
      title: "Do you really want delete?",
      message: "This action can't be undone.",
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Delete",
          color: "secondary",
          onClick: () => {
            deleteCar();
          },
        },
      ],
    });
  }, [deleteCar, openAlert]);

  const handleClose = React.useCallback(() => {
    setExpanded(false);
  }, []);

  return (
    <RbFlexTableRow
      renderExpand={() => (
        <CarForm car={car} onSave={handleClose} onCancel={handleClose} />
      )}
      expanded={expanded}
    >
      <Hidden smDown>
        <RbFlexTableCell flex="0 60px">{car?.id}</RbFlexTableCell>
      </Hidden>
      <RbFlexTableCell>{car?.model}</RbFlexTableCell>
      <Hidden smDown>
        <RbFlexTableCell>{car?.color}</RbFlexTableCell>
      </Hidden>
      <Hidden smDown>
        <RbFlexTableCell flex="0 100px" align="right">
          {car?.year}
        </RbFlexTableCell>
      </Hidden>
      <RbFlexTableCell flex="0 100px" align="right">
        {formatCurrency(car?.price)}
      </RbFlexTableCell>
      <RbFlexTableCell flex={actionsFlex} align="right">
        <RbActions limit={actionsLimit} compacted color="default">
          <RbButtonAction Icon={EditIcon} text="Edit" onClick={handleEdit} />
          <RbButtonAction
            Icon={DeleteIcon}
            text="Delete"
            onClick={handleDelete}
          />
        </RbActions>
      </RbFlexTableCell>
    </RbFlexTableRow>
  );
}

CarRow.propTypes = {
  car: PropTypes.object.isRequired,
};

export default React.memo(CarRow);
